// ChildrenTab.jsx
//
// "Діти в садочках": зведення по Києву та районах, з розгортанням до
// садочків і деталізацією за статтю/пільгою/труднощами.
//
// Зміни щодо попередньої версії:
//
// 1. disabilities більше не завантажуються тут — приходять пропсом з index.jsx.
//
// 2. Усунено подвійний запрос при зміні року. Раніше було два useEffect:
//    - useEffect([filters.year])  → setMonths(...)
//    - useEffect([filters, months]) → fetchData(...)
//    Коли рік змінювався, другий ефект спрацьовував двічі: одразу зі старим
//    months, потім знову — коли months оновився. Тепер months обчислюється
//    прямо всередині єдиного useEffect([filters]) без збереження у стані,
//    тому fetchData викликається рівно один раз.
import React, { useEffect, useState, useMemo } from 'react';
import { Pie, Bar } from 'react-chartjs-2';
import { useChildrenData } from './hooks/useStatisticsData';
import { useDragScroll } from './hooks/useDragScroll';
import { useIsMobile } from './hooks/useIsMobile';
import { resolveMultiParam, resolveBenefitsParam, statisticsApi } from './statisticsApi';
import { aggregateDistribution } from './utils/aggregateDistribution';
import { externalTooltipHandler } from './utils/externalTooltip';
import ChildrenBreakdownTable from './ChildrenBreakdownTable';
import ChartDonut from './ChartDonut';
import MobileSheet from './MobileSheet';
import './chartConfig';

const CITY_ALL_KEY = '__all_kyiv__';

// Список місяців навчального року (незмінний, виносимо за компонент)
const MONTH_NAMES = [
  'Вересень', 'Жовтень', 'Листопад', 'Грудень', 'Січень', 'Лютий',
  'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень',
];

// Обчислює список доступних місяців для обраного навчального року.
// Якщо рік поточний — обрізаємо до вже минулих місяців.
function computeAvailableMonths(yearLabel) {
  const selectedYear = parseInt((yearLabel || '').split('→')[0].trim());
  const currentYear  = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  if (selectedYear === currentYear) {
    const monthIndex = currentMonth < 8 ? currentMonth + 5 : currentMonth - 7;
    return MONTH_NAMES.slice(0, monthIndex + 1);
  }
  return MONTH_NAMES;
}

const ChildrenTab = ({ filters, onLoading, onError, disabilities = [] }) => {
  const { data, loading, error, fetchData } = useChildrenData();
  const [tableData, setTableData]                       = useState([]);
  const [totalRow, setTotalRow]                         = useState(null);
  const [dataParams, setDataParams]                     = useState(null);
  const [openDistrict, setOpenDistrict]                 = useState(null);
  const [cityDistribution, setCityDistribution]         = useState(null);
  const [cityDistributionLoading, setCityDistributionLoading] = useState(false);

  const { ref: scrollRef, listeners: dragListeners } = useDragScroll();
  const isMobile = useIsMobile();

  // ── Основний ефект: запускає дані при зміні будь-якого фільтра ──────────────
  // months обчислюються тут же, без окремого useState — це усуває подвійний
  // запрос, який виникав при зміні року через ланцюжок двох useEffect.
  useEffect(() => {
    if (!filters.year || !filters.asOf) return;

    const availableMonths = computeAvailableMonths(filters.year);
    const monthIndex = availableMonths.findIndex(m => m === filters.asOf);
    if (monthIndex === -1) return; // обраний місяць недоступний для цього року

    const yearNum       = parseInt(filters.year.split('→')[0].trim());
    const period_month  = monthIndex + 1; // 1-12
    const period_month_calc = period_month > 3 ? period_month - 4 : period_month + 8;
    const year_to = period_month_calc < 8 ? yearNum + 1 : yearNum;

    const params = {
      period_start_year: yearNum,
      date_from: new Date(year_to, period_month_calc, 1).toISOString(),
      date_to:   new Date(year_to, period_month_calc + 1, 0).toISOString(),
      status_mr:       filters.registration === 'kyiv_reg' ? 'true' : 'all',
      disability_type: resolveMultiParam(filters.disabilityType),
      benefits:        resolveBenefitsParam(filters.benefit),
      type_place:      filters.placeType !== 'all' ? filters.placeType : 'all',
    };

    setDataParams(params);
    fetchData(params);

    // Зведений розподіл за статтю/пільгою/труднощами по всьому місту
    setCityDistributionLoading(true);
    statisticsApi.getChildrenDistributionByKindergarten(null, params)
      .then(res => setCityDistribution(res.data && Array.isArray(res.data) ? res.data : []))
      .catch(() => setCityDistribution([]))
      .finally(() => setCityDistributionLoading(false));
  }, [filters, fetchData]); // тільки filters — months більше не є окремим станом

  useEffect(() => { if (onLoading) onLoading(loading); }, [loading, onLoading]);
  useEffect(() => { if (onError && error) onError(error); }, [error, onError]);

  useEffect(() => {
    if (!data || !data.districts) {
      setTableData([]);
      setTotalRow(null);
      return;
    }

    const rows = [];
    Object.keys(data.districts).sort().forEach(district => {
      const ages = data.districts[district];
      const row = {
        district,
        age0_1: ages[1970] || 0, age1_2: ages[1971] || 0, age2_3: ages[1972] || 0,
        age3_4: ages[1973] || 0, age4_5: ages[1974] || 0, age5_6: ages[1975] || 0,
        age6_7: ages[1976] || 0, age7_8: ages[1977] || 0, age8_9: ages[1978] || 0,
      };
      row.total    = row.age0_1 + row.age1_2 + row.age2_3 + row.age3_4 + row.age4_5 + row.age5_6 + row.age6_7 + row.age7_8 + row.age8_9;
      row.total3_6 = row.age3_4 + row.age4_5 + row.age5_6;
      rows.push(row);
    });

    const t = data.totals;
    const totals = {
      district: 'По Києву',
      age0_1: t[1970] || 0, age1_2: t[1971] || 0, age2_3: t[1972] || 0,
      age3_4: t[1973] || 0, age4_5: t[1974] || 0, age5_6: t[1975] || 0,
      age6_7: t[1976] || 0, age7_8: t[1977] || 0, age8_9: t[1978] || 0,
    };
    totals.total    = totals.age0_1 + totals.age1_2 + totals.age2_3 + totals.age3_4 + totals.age4_5 + totals.age5_6 + totals.age6_7 + totals.age7_8 + totals.age8_9;
    totals.total3_6 = totals.age3_4 + totals.age4_5 + totals.age5_6;

    setTableData(rows);
    setTotalRow(totals);
  }, [data]);

  const kgRows = useMemo(() => {
    if (!data || !data.kindergartens) return [];
    return Object.values(data.kindergartens).map(kg => {
      const row = {
        id: kg.kgId, kgnumber: kg.kgNumber, district: kg.district,
        age0_1: kg[1970] || 0, age1_2: kg[1971] || 0, age2_3: kg[1972] || 0,
        age3_4: kg[1973] || 0, age4_5: kg[1974] || 0, age5_6: kg[1975] || 0,
        age6_7: kg[1976] || 0, age7_8: kg[1977] || 0, age8_9: kg[1978] || 0,
      };
      row.total    = row.age0_1 + row.age1_2 + row.age2_3 + row.age3_4 + row.age4_5 + row.age5_6 + row.age6_7 + row.age7_8 + row.age8_9;
      row.total3_6 = row.age3_4 + row.age4_5 + row.age5_6;
      return row;
    });
  }, [data]);

  const kgRowsByDistrict = useMemo(() => {
    const groups = {};
    kgRows.forEach(row => {
      if (!groups[row.district]) groups[row.district] = [];
      groups[row.district].push(row);
    });
    return groups;
  }, [kgRows]);

  const allKgRows = kgRows;

  const { sexTotals, benefitTotals, disabilityTotals, total: distributionTotal } = useMemo(
    () => aggregateDistribution(cityDistribution),
    [cityDistribution]
  );

  const disabilityLabel = (id) => {
    if (id === 'none') return 'без труднощів';
    const d = disabilities.find(x => x._id === id);
    return d ? (d.label || d.name) : id;
  };

  const disabilityEntries = useMemo(
    () => Object.entries(disabilityTotals).sort((a, b) => b[1] - a[1]),
    [disabilityTotals]
  );

  const SEX_COLORS        = { 'ч': '#3498db', 'ж': '#e8a898' };
  const BENEFIT_COLORS    = { yes: '#58d68d', no: '#d1d5db' };
  const DISABILITY_COLORS = ['#9b59b6', '#f4d03f', '#e74c3c', '#1abc9c', '#f7dc6f', '#85c1e9', '#c39bd3', '#a9dfbf'];

  const sexChartData = {
    labels: ['Хлопчики', 'Дівчатка'],
    datasets: [{ data: [sexTotals['ч'], sexTotals['ж']], backgroundColor: [SEX_COLORS['ч'], SEX_COLORS['ж']], borderWidth: 0 }],
  };
  const benefitChartData = {
    labels: ['З пільгою', 'Без пільги'],
    datasets: [{ data: [benefitTotals.yes, benefitTotals.no], backgroundColor: [BENEFIT_COLORS.yes, BENEFIT_COLORS.no], borderWidth: 0 }],
  };
  const disabilityChartData = {
    labels: disabilityEntries.map(([id]) => disabilityLabel(id)),
    datasets: [{
      data: disabilityEntries.map(([, c]) => c),
      backgroundColor: disabilityEntries.map((_, i) => DISABILITY_COLORS[i % DISABILITY_COLORS.length]),
      borderWidth: 0,
    }],
  };

  const districtChartData = {
    labels: tableData.map(r => r.district),
    datasets: [
      { label: 'Разом',     data: tableData.map(r => r.total),    backgroundColor: '#93c5fd', borderRadius: 3 },
      { label: 'Разом 3-6', data: tableData.map(r => r.total3_6), backgroundColor: '#0069ff', borderRadius: 3 },
    ],
  };
  const districtChartOptions = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: 'top' }, tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toLocaleString()}` } } },
    scales: { x: { beginAtZero: true } },
  };
  const districtChartHeight = Math.max(320, tableData.length * 42);

  const pieData = {
    labels: ['0-1 рік', '1-2 роки', '2-3 роки', '3-4 роки', '4-5 років', '5-6 років', '6-7 років', '7-8 років', '8-9 років'],
    datasets: [{
      data: totalRow
        ? [totalRow.age0_1, totalRow.age1_2, totalRow.age2_3, totalRow.age3_4, totalRow.age4_5, totalRow.age5_6, totalRow.age6_7, totalRow.age7_8, totalRow.age8_9]
        : [0, 0, 0, 0, 0, 0, 0, 0, 0],
      backgroundColor: ['#e8a898', '#f4d03f', '#e74c3c', '#58d68d', '#a9dfbf', '#f7dc6f', '#3498db', '#9b59b6', '#1abc9c'],
      borderWidth: 0,
    }],
  };
  const pieOptions = {
    responsive: true, maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false, external: externalTooltipHandler, callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw} дітей` } },
    },
    cutout: '60%',
  };

  if (loading && !tableData.length)    return <div className="text-center py-5">Завантаження даних...</div>;
  if (error && !tableData.length)      return <div className="text-center py-5 text-danger">Помилка: {error}</div>;
  if (!tableData.length && !loading)   return <div className="text-center py-5 text-muted">Немає даних за обраними параметрами</div>;

  return (
    <>
      <div className="publicstatistic-statistics-table-section">
        <h3 className="publicstatistic-section-title">Діти в садочках</h3>
        <div className="mb-2" style={{ fontSize: '0.82rem', color: '#666' }}>
          Натисніть на «По Києву» або на район, щоб побачити розподіл по садочках
        </div>
        <div ref={scrollRef} className="publicstatistic-table-responsive" style={{ cursor: 'grab', overflowX: 'auto' }} {...dragListeners}>
          <table id="pubstat-children-table" className="table publicstatistic-statistics-table">
            <thead>
              <tr>
                <th>Район</th><th>Разом</th><th>Разом 3-6</th>
                <th>0-1 рік</th><th>1-2 роки</th><th>2-3 роки</th><th>3-4 роки</th>
                <th>4-5 років</th><th>5-6 років</th><th>6-7 років</th><th>7-8 років</th><th>8-9 років</th>
              </tr>
            </thead>
            <tbody>
              {totalRow && (
                <React.Fragment>
                  <tr
                    onClick={() => setOpenDistrict(prev => prev === CITY_ALL_KEY ? null : CITY_ALL_KEY)}
                    style={{ background: '#eef2ff', borderBottom: '2px solid #c7d2fe', cursor: 'pointer', borderLeft: openDistrict === CITY_ALL_KEY ? '3px solid #0069ff' : '3px solid transparent' }}
                    title="Натисніть для перегляду всіх садочків міста"
                  >
                    <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'left', verticalAlign: 'middle', fontSize: '0.875rem' }}>
                      <span style={{ marginRight: 4, fontSize: '0.7rem', color: openDistrict === CITY_ALL_KEY ? '#0069ff' : '#6b82c4' }}>
                        {openDistrict === CITY_ALL_KEY ? '▼' : '▶'}
                      </span>
                      <strong>{totalRow.district}</strong>
                    </td>
                    {['total','total3_6','age0_1','age1_2','age2_3','age3_4','age4_5','age5_6','age6_7','age7_8','age8_9'].map(k => (
                      <td key={k} style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}>
                        <strong>{totalRow[k].toLocaleString()}</strong>
                      </td>
                    ))}
                  </tr>
                  {openDistrict === CITY_ALL_KEY && !isMobile && (
                    <tr>
                      <td colSpan={12} style={{ padding: 0, background: '#fafbff', borderLeft: '3px solid #0069ff' }}>
                        <ChildrenBreakdownTable rows={allKgRows} dataParams={dataParams} disabilities={disabilities} showDistrict scopeLabel="місті" />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )}

              {tableData.map((row, index) => (
                <React.Fragment key={row.district}>
                  <tr
                    className={index % 2 === 0 ? 'publicstatistic-row-even' : ''}
                    onClick={() => setOpenDistrict(prev => prev === row.district ? null : row.district)}
                    style={{ cursor: 'pointer', borderLeft: openDistrict === row.district ? '3px solid #0069ff' : '3px solid transparent', background: openDistrict === row.district ? '#f0f4ff' : undefined }}
                    title="Натисніть для перегляду садочків району"
                  >
                    <td className="publicstatistic-district-name">
                      <span style={{ marginRight: 4, fontSize: '0.7rem', color: openDistrict === row.district ? '#0069ff' : '#9ca3af' }}>
                        {openDistrict === row.district ? '▼' : '▶'}
                      </span>
                      {row.district}
                    </td>
                    <td className="publicstatistic-total-cell">{row.total.toLocaleString()}</td>
                    <td className="publicstatistic-total-cell">{row.total3_6.toLocaleString()}</td>
                    <td>{row.age0_1.toLocaleString()}</td><td>{row.age1_2.toLocaleString()}</td>
                    <td>{row.age2_3.toLocaleString()}</td><td>{row.age3_4.toLocaleString()}</td>
                    <td>{row.age4_5.toLocaleString()}</td><td>{row.age5_6.toLocaleString()}</td>
                    <td>{row.age6_7.toLocaleString()}</td><td>{row.age7_8.toLocaleString()}</td>
                    <td>{row.age8_9.toLocaleString()}</td>
                  </tr>
                  {openDistrict === row.district && !isMobile && (
                    <tr>
                      <td colSpan={12} style={{ padding: 0, background: '#fafbff', borderLeft: '3px solid #0069ff' }}>
                        <ChildrenBreakdownTable rows={kgRowsByDistrict[row.district] || []} dataParams={dataParams} disabilities={disabilities} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isMobile && (
        <MobileSheet
          isOpen={!!openDistrict}
          onClose={() => setOpenDistrict(null)}
          title={openDistrict === CITY_ALL_KEY ? 'Всі садочки міста' : 'Садочки району'}
          subtitle={openDistrict === CITY_ALL_KEY ? undefined : openDistrict}
        >
          <ChildrenBreakdownTable
            rows={openDistrict === CITY_ALL_KEY ? allKgRows : (kgRowsByDistrict[openDistrict] || [])}
            dataParams={dataParams}
            disabilities={disabilities}
            showDistrict={openDistrict === CITY_ALL_KEY}
            scopeLabel={openDistrict === CITY_ALL_KEY ? 'місті' : 'районі'}
            capHeight={false}
          />
        </MobileSheet>
      )}

      <div className="publicstatistic-charts-section">
        <div className="row">
          <div className="col-lg-5">
            <h4 className="publicstatistic-chart-title">Розподіл дітей за віком</h4>
            <div className="publicstatistic-custom-pie-chart">
              <div className="publicstatistic-pie-chart-center">
                <span className="publicstatistic-chart-total">{totalRow?.total.toLocaleString() || 0}</span>
                <span className="publicstatistic-chart-total-label">всього дітей</span>
              </div>
              <Pie data={pieData} options={pieOptions} />
            </div>
            <div className="publicstatistic-chart-legend publicstatistic-chart-legend-multi">
              {pieData.labels.map((label, i) => (
                <div key={i} className="publicstatistic-legend-item">
                  <span className="publicstatistic-legend-color" style={{ backgroundColor: pieData.datasets[0].backgroundColor[i] }}></span>
                  <span>{label}: {pieData.datasets[0].data[i].toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="col-lg-7">
            <h4 className="publicstatistic-chart-title">Кількість дітей за районами</h4>
            <div style={{ position: 'relative', height: districtChartHeight }}>
              <Bar data={districtChartData} options={districtChartOptions} />
            </div>
          </div>
        </div>

        <hr className="my-4" />

        <div className="row">
          <div className="col-12">
            <h4 className="publicstatistic-chart-title">Розподіл дітей по місту</h4>
            {cityDistributionLoading ? (
              <div className="text-center py-4 text-muted" style={{ fontSize: '0.85rem' }}>Завантаження…</div>
            ) : distributionTotal > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, justifyContent: 'center' }}>
                <ChartDonut title="За статтю" total={distributionTotal} data={sexChartData}
                  legend={[
                    { label: 'Хлопчики', value: sexTotals['ч'], color: SEX_COLORS['ч'] },
                    { label: 'Дівчатка', value: sexTotals['ж'], color: SEX_COLORS['ж'] },
                  ]}
                />
                <ChartDonut title="За наявністю пільги" total={distributionTotal} data={benefitChartData}
                  legend={[
                    { label: 'З пільгою', value: benefitTotals.yes, color: BENEFIT_COLORS.yes },
                    { label: 'Без пільги', value: benefitTotals.no, color: BENEFIT_COLORS.no },
                  ]}
                />
                <ChartDonut title="За типом труднощів" total={distributionTotal} data={disabilityChartData}
                  legend={disabilityEntries.map(([id, count], i) => ({
                    label: disabilityLabel(id), value: count, color: DISABILITY_COLORS[i % DISABILITY_COLORS.length],
                  }))}
                />
              </div>
            ) : (
              <div className="text-center py-4 text-muted" style={{ fontSize: '0.85rem' }}>Немає даних за обраними параметрами</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChildrenTab;